import { useState, useEffect, createContext } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";

// Components
import Navigation from "./components/Navigation";
import Home from "./pages/Home";
import Feed from "./pages/Feed";
import Groups from "./pages/Groups";
import MyNetwork from "./pages/MyNetwork";
import Messages from "./pages/Messages";
import Events from "./pages/Events";
import Resources from "./pages/Resources";
import Tenders from "./pages/Tenders";
import MyTenders from "./pages/MyTenders";
import Profile from "./pages/Profile";
import Login from "./pages/Login";
import Register from "./pages/Register";
import AdminDashboard from "./pages/AdminDashboard";
import UserGuide from "./pages/UserGuide";

// Context
import { LanguageProvider } from "./contexts/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Debug logging
console.log('🔧 Frontend Configuration:');
console.log('📍 BACKEND_URL:', BACKEND_URL);
console.log('🔗 API URL:', API);
console.log('🌐 All env vars:', process.env);

// API Client setup
axios.defaults.baseURL = API;

// Auth Context
export const AuthContext = createContext({
  user: null,
  token: null,
  login: () => {},
  logout: () => {},
  loading: true
});

function App() {
  // Authentication state with better sequencing
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [authReady, setAuthReady] = useState(false);
  const [userGuideSection, setUserGuideSection] = useState('getting-started');

  // Global error handler for browser extension issues
  useEffect(() => {
    const handleGlobalError = (event) => {
      // Handle browser extension errors gracefully
      if (event.message && event.message.includes('Extension context invalidated')) {
        console.warn('🔌 Browser extension context invalidated - ignoring error');
        event.preventDefault();
        return;
      }
      
      if (event.message && event.message.includes('message port closed')) {
        console.warn('🔌 Browser extension message port closed - ignoring error');
        event.preventDefault();
        return;
      }
    };

    const handleUnhandledRejection = (event) => {
      // Handle network-related promise rejections
      if (event.reason && event.reason.message && 
          event.reason.message.includes('NetworkError')) {
        console.warn('🌐 Network error detected - handling gracefully');
        event.preventDefault();
        return;
      }
    };

    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleGlobalError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  // Configure axios defaults with retry logic
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Check authentication with improved error handling and retry logic
  useEffect(() => {
    const checkAuth = async (retryCount = 0) => {
      const savedToken = localStorage.getItem('token');
      
      if (!savedToken) {
        console.log('🚫 No saved token found');
        setLoading(false);
        setAuthReady(true);
        return;
      }

      console.log('🔍 Checking saved token authentication...');

      try {
        // Set axios defaults immediately for consistency
        axios.defaults.baseURL = process.env.REACT_APP_BACKEND_URL || '';
        axios.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;

        const response = await axios.get('/api/auth/me');
        
        if (response.data) {
          console.log('✅ Token validation successful:', response.data.email);
          setUser(response.data);
          setToken(savedToken);
          
          // Add small delay to ensure all components are ready
          setTimeout(() => {
            setAuthReady(true);
            setLoading(false);
          }, 100);
        } else {
          throw new Error('Invalid user data received');
        }
      } catch (error) {
        console.error('❌ Token validation failed:', error);
        
        // Handle browser extension errors or network issues with retry
        const isNetworkError = !error.response && error.code !== 'ERR_BAD_REQUEST';
        const canRetry = retryCount < 2 && isNetworkError;
        
        if (canRetry) {
          console.log(`🔄 Retrying authentication (attempt ${retryCount + 1}/2)...`);
          setTimeout(() => {
            checkAuth(retryCount + 1);
          }, 1000 * (retryCount + 1)); // Exponential backoff
          return;
        }
        
        // Clear invalid token after retries exhausted
        localStorage.removeItem('token');
        delete axios.defaults.headers.common['Authorization'];
        setUser(null);
        setToken(null);
        setAuthReady(true);
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = (userData, authToken) => {
    setUser(userData);
    setToken(authToken);
    localStorage.setItem('token', authToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
  };

  const handleUserGuideSection = (sectionId) => {
    setUserGuideSection(sectionId);
  };

  const updateUser = (updatedUserData) => {
    setUser(prevUser => ({ ...prevUser, ...updatedUserData }));
  };

  const authContext = {
    user,
    token,
    login,
    logout,
    updateUser,
    loading,
    authReady
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading Labamu Hub...</p>
        </div>
      </div>
    );
  }

  return (
    <LanguageProvider>
      <AuthContext.Provider value={authContext}>
        <div className="App">
          <BrowserRouter>
            {user ? (
              // Authenticated Routes with Sidebar Layout
              <Navigation onUserGuideSection={handleUserGuideSection}>
                <main className="flex-1 bg-gray-50 overflow-auto">
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/feed" element={<Feed />} />
                    <Route path="/groups" element={<Groups />} />
                    <Route path="/network" element={<MyNetwork />} />
                    <Route path="/messages" element={<Messages />} />
                    <Route path="/events" element={<Events />} />
                    <Route path="/resources" element={<Resources />} />
                    <Route path="/tenders" element={<Tenders />} />
                    <Route path="/my-tenders" element={<MyTenders />} />
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/admin" element={<AdminDashboard />} />
                    <Route path="/guide" element={<UserGuide activeSection={userGuideSection} onSectionChange={setUserGuideSection} />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </main>
              </Navigation>
            ) : (
              // Unauthenticated Routes
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="*" element={<Navigate to="/login" replace />} />
              </Routes>
            )}
          </BrowserRouter>
        </div>
      </AuthContext.Provider>
    </LanguageProvider>
  );
}

export default App;